import {
    type DataPresentation,
    type DataPresentationReceipt,
    dataPresentationSchema,
    parseDataPresentationOutput,
} from '@apps/web/components/phoebe_chat/bash_presentation';
import { type DynamicWidgetBuildContext } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_registry';
import {
    buildBindingPresentation,
    dynamicWidgetEventSourceSchema,
    DynamicWidgetModelBuilder,
    dynamicWidgetResourceSourceSchema,
    getDynamicWidgetSourceId,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_schema';
import { type ToolExecutionEntry } from '@apps/web/components/phoebe_chat_view/transcript_types';

const WORKSPACE_FILE_API_PATH =
    '/organizations/{organization_id}/phoebe_agent_runs/{phoebe_agent_run_id}/workspace-files/{file_path}';

export { dataPresentationSchema };
export type { DataPresentation };

export type DataPresentationArtifactBuild = {
    readonly receipt: DataPresentationReceipt;
    readonly workspacePath: string;
    readonly eventSourceId: string;
    readonly resourceSourceId: string;
    readonly builder: DynamicWidgetModelBuilder;
};

export function matchesDataPresentation(
    entry: ToolExecutionEntry,
    presentation: DataPresentation,
): boolean {
    return (
        parseDataPresentationOutput(entry.name, entry.output)?.receipt
            .presentation === presentation
    );
}

export function buildDataPresentationArtifact(
    entry: ToolExecutionEntry,
    context: DynamicWidgetBuildContext,
    presentation: DataPresentation,
): DataPresentationArtifactBuild | null {
    if (
        (entry.name !== 'run_bash' &&
            entry.name !== 'render_dynamic_ui' &&
            entry.name !== 'query') ||
        entry.callId === null ||
        !entry.toolCallEventId ||
        !entry.runId
    ) {
        return null;
    }
    const parsed = parseDataPresentationOutput(entry.name, entry.output);
    if (parsed === null || parsed.receipt.presentation !== presentation) {
        return null;
    }
    const { receipt, workspacePath } = parsed;
    const eventSource = dynamicWidgetEventSourceSchema.parse({
        kind: 'event_source',
        toolName: entry.name,
        callId: entry.callId,
        toolCallEventId: entry.toolCallEventId,
        toolOutputEventId: entry.id,
        phase: 'output',
        capturedAt: entry.createdAt,
        organizationId: context.organizationId,
        mode: context.mode,
    });
    const resourceSource = dynamicWidgetResourceSourceSchema.parse({
        kind: 'resource_source',
        resourceType: 'agent_run_workspace_file',
        resourceId: `${entry.runId}:${workspacePath}`,
        apiPath: WORKSPACE_FILE_API_PATH,
        fetchedAt: null,
        organizationId: context.organizationId,
        mode: context.mode,
    });
    const eventSourceId = getDynamicWidgetSourceId(eventSource);
    const resourceSourceId = getDynamicWidgetSourceId(resourceSource);
    const builder = new DynamicWidgetModelBuilder([
        eventSource,
        resourceSource,
    ]);
    builder.addSourceField({
        id: 'rowCount',
        sourceId: eventSourceId,
        path: '/presentation_artifact/row_count',
        rawValue: receipt.row_count,
        value: receipt.row_count,
        renderedValue: String(receipt.row_count),
        presentation: buildBindingPresentation({
            section: 'card',
            label: 'rowCount',
        }),
    });
    builder.addSourceField({
        id: 'workspaceFile.path',
        sourceId: eventSourceId,
        path: '/presentation_artifact/artifact_path',
        rawValue: receipt.artifact_path,
        value: workspacePath,
        renderedValue: workspacePath,
        presentation: buildBindingPresentation({
            section: 'workspaceFile',
            label: 'path',
        }),
    });
    builder.addSourceField({
        id: 'workspaceFile.byteSize',
        sourceId: eventSourceId,
        path: '/presentation_artifact/byte_size',
        rawValue: receipt.byte_size,
        value: receipt.byte_size,
        renderedValue: String(receipt.byte_size),
        presentation: buildBindingPresentation({
            section: 'workspaceFile',
            label: 'byteSize',
        }),
    });
    builder.addSourceField({
        id: 'workspaceFile.columns',
        sourceId: eventSourceId,
        path: '/presentation_artifact/columns',
        rawValue: receipt.columns,
        value: receipt.columns,
        renderedValue: receipt.columns.join(', '),
        presentation: buildBindingPresentation({
            section: 'workspaceFile',
            label: 'columns',
        }),
    });
    builder.addLiveField({
        id: 'resourceRows',
        sourceIds: [resourceSourceId],
        paths: ['/rows'],
        presentation: buildBindingPresentation({
            section: 'rows',
            label: 'resourceRows',
        }),
    });
    return {
        receipt,
        workspacePath,
        eventSourceId,
        resourceSourceId,
        builder,
    };
}
